import { useState, useRef } from 'react'
import { Flex } from '@radix-ui/themes'
import { useLoaderData } from 'react-router'
import { useEventListener } from 'usehooks-ts'
import { addDays, format } from 'date-fns'
import { createClient } from '@/api-client'
import { Me } from '~/api-client/Me'
import { SubscriptionSchedules } from '~/api-client/SubscriptionSchedules'
import { SubscriptionSchedule } from '@/api-client/data-contracts'
import { styles } from './styles.css'
import { HeroSection } from '~/components/subscription/lp/hero-section'
import { SummarySection } from '~/components/subscription/lp/summary-section'
import { FeaturesSection } from '~/components/subscription/lp/features-section'
import { HowtoSection } from '~/components/subscription/lp/howto-section'
import { VaseSection } from '~/components/subscription/lp/vase-section'
import { PlanSection } from '~/components/subscription/lp/plan-section'
import { NutrientSection } from '~/components/subscription/lp/nutrient-section'
import { AboutSection } from '~/components/subscription/lp/about-section'
import { TailSection } from '~/components/subscription/lp/tail-section'
import { FooterSection } from '~/components/subscription/lp/footer-section'
import { FaqSection } from '~/components/subscription/lp/faq-section'
import { SubscribeButton } from '~/components/subscription/lp/subscribe-button'
import { DesktopSidebar } from '~/components/subscription/lp/desktop-sidebar'
import { Notification } from '~/components/subscription/notification'

export const loader = async () => {
  const account = await fetchMe()
  const schedules = await fetchSchedules()
  const pastSchedules = await fetchPastSchedules()

  return [account, schedules, pastSchedules] as const
}

async function fetchMe() {
  return createClient(Me)
    .getMe()
    .catch(() => ({ data: { data: null } }))
}

async function fetchSchedules() {
  return createClient(SubscriptionSchedules).getSubscriptionSchedules({ upcoming: '1' })
}

function fetchPastSchedules() {
  return createClient(SubscriptionSchedules).getSubscriptionSchedules({ upcoming: '0', with_image: '1' })
}

function findSubscribableSchedule(schedules: SubscriptionSchedule[], included) {
  const inventory = included.find((item) => item.type === 'subscription_inventory' && !item.attributes.is_soldout)
  const schedule = schedules.find((schedule) => schedule.relationships.inventory.data.id === inventory.id)

  return { inventory, schedule }
}

export const Subscription: React.FC = () => {
  const [
    {
      data: { data: account },
    },
    {
      data: { data: schedules, included },
    },
    {
      data: { data: pastSchedules },
    },
  ] = useLoaderData<typeof loader>()

  const page = useRef<HTMLDivElement>(null)
  const featuresSection = useRef<HTMLElement>(null)
  const [bottomFixedVisible, setBottomButtonVisible] = useState(false)
  const { schedule, inventory } = findSubscribableSchedule(schedules, included)

  const [deliverAt] = useState(() => {
    if (schedule) {
      const date = new Date(schedule.attributes.ship_at * 1000)
      const min = addDays(date, 1)
      const max = addDays(date, 2)

      return `${format(min, 'M/d')}〜${format(max, 'M/d')}`
    } else {
      return null
    }
  })

  useEventListener('scroll', () => {
    const top = featuresSection.current?.getBoundingClientRect()?.top

    if (top) {
      if (top < 0) {
        setBottomButtonVisible(true)
      } else {
        setBottomButtonVisible(false)
      }
    }
  })

  const handleSidebarClick = (e: React.MouseEvent) => {
    e.preventDefault()

    const targetId = e.currentTarget.getAttribute('href')?.substring(1)
    const targetElement = document.getElementById(targetId)

    history.pushState(null, null, '#' + targetId)
    page.current?.scrollTo({ top: targetElement?.offsetTop - 80, behavior: 'smooth' })
  }

  return (
    <Flex direction="row" align="start" justify="center" className={styles.screenContainer}>
      <DesktopSidebar
        onClick={handleSidebarClick}
        renderButton={() => {
          return (
            <Flex gap="16px" direction="column" align="center">
              <Flex className={styles.nutrientPopup} align="center" justify="center" gap="4px">
                <span>🎁</span>
                <span>初回特典！お花の栄養剤5袋プレゼント中</span>
              </Flex>
              <SubscribeButton
                title="定期便をはじめる"
                subtitle={`初回のお届け日：${deliverAt}`}
                disabled={!deliverAt}
                href={`/subscription/new?inventory_ids=${inventory.id}`}
              />
            </Flex>
          )
        }}
      />
      <article className={styles.pageContainer} ref={page}>
        <Flex direction="column" justify="start" width="100%">
          <HeroSection isLoggedIn={!!account} />
          <SummarySection />
          <div className={`${styles.subscribeButton} ${styles.mobileOnly}`}>
            <SubscribeButton
              title="定期便をはじめる"
              subtitle={`初回のお届け日：${deliverAt}`}
              disabled={!deliverAt}
              href={`/subscription/new?inventory_ids=${inventory.id}`}
            />
          </div>
          <FeaturesSection ref={featuresSection} schedules={pastSchedules} />
          <HowtoSection />
          <VaseSection />
          <PlanSection />
          <NutrientSection />
          <AboutSection />
          <FaqSection />
          <TailSection />
          <div className={styles.mobileOnly}>
            <FooterSection />
          </div>
        </Flex>
        <div className={[styles.subscribeButton, styles.bottomFixed, styles.mobileOnly, bottomFixedVisible && styles.bottomFixedVisible].join(' ')}>
          <Flex className={styles.nutrientPopup} align="center" justify="center" gap="4px">
            <span>🎁</span>
            <span>初回特典！お花の栄養剤5袋プレゼント中</span>
          </Flex>
          <SubscribeButton
            title="定期便をはじめる"
            subtitle={`初回のお届け日：${deliverAt}`}
            disabled={!deliverAt}
            href={`/subscription/new?inventory_ids=${inventory.id}`}
          />
        </div>
      </article>
      <Flex direction="column" align="end" justify="end" height="100%">
        <span className={styles.copyRight}>©FLOWER</span>
      </Flex>
      <Notification />
    </Flex>
  )
}
